import { useState, useEffect, useCallback } from 'react'
import { billingService, BillingStatus, PlanTier, tierAtLeast } from '../services/billing'

interface UsePlanReturn {
  status: BillingStatus | null
  loading: boolean
  /** true if the org's tier is >= the given minimum */
  can: (minimum: PlanTier) => boolean
  /** true if the trial has expired */
  isExpired: boolean
  /** days left in trial (null if not on trial) */
  trialDaysLeft: number | null
  /** refetch billing status from the server */
  refresh: () => void
}

// Module-level cache so all components share one fetch per session.
let _cache: BillingStatus | null = null
let _listeners: Array<() => void> = []
let _fetching = false

function notify() {
  _listeners.forEach(fn => fn())
}

async function fetchStatus() {
  if (_fetching) return
  _fetching = true
  try {
    _cache = await billingService.getStatus()
    notify()
  } catch {
    // Network failure — leave cache as-is (or null on first load).
  } finally {
    _fetching = false
  }
}

export function usePlan(): UsePlanReturn {
  const [, forceUpdate] = useState(0)
  const [loading, setLoading] = useState(_cache === null)

  const refresh = useCallback(() => {
    _cache = null
    setLoading(true)
    fetchStatus().then(() => setLoading(false))
  }, [])

  useEffect(() => {
    const listener = () => {
      setLoading(false)
      forceUpdate(n => n + 1)
    }
    _listeners.push(listener)

    if (_cache === null && !_fetching) {
      fetchStatus()
    }

    return () => {
      _listeners = _listeners.filter(l => l !== listener)
    }
  }, [])

  const status = _cache

  return {
    status,
    loading,
    can: (minimum: PlanTier) => {
      if (!status) return true // optimistic while loading
      return tierAtLeast(status.effective_tier ?? status.plan_tier, minimum)
    },
    isExpired: status?.trial_expired ?? false,
    trialDaysLeft: status?.trial_days_remaining ?? null,
    refresh,
  }
}
